import React, { ReactElement, RefObject, RefCallback } from 'react';
import SelectionInput, { TSelectionInputBadge } from './SelectionInput';
import { Checkbox as componentName } from './name';
import { TAsset, TIcon } from '../../types/TStyleVariables';

export type TCheckbox = {
  /** value of the checkbox dom input*/
  value: string;
  /** The id of the checkbox input, also used on the label as htmlFor */
  id?: string;
  /** Whether or not it is checked */
  isChecked: boolean;
  /** The label text. Also passed into aria-label attribute on the input itself*/
  labelText?: string;
  // ** Count of items in a formatted string */
  counter?: string;
  /** Only visible on mobile + stacked */
  icon?: TIcon;
  /** Secondary text below the label. Accepts a string or ReactNode to support inline links. */
  helperText?: React.ReactNode;
  /** On component change */
  onChange?: React.FormEventHandler<HTMLInputElement>;
  className?: string;
  /** External `ref` */
  inputRef?: RefObject<HTMLInputElement> | RefCallback<HTMLInputElement>;
  disabled?: boolean;
  ariaLabel?: string;
  /**
   * Deprecated — use `badge` instead.
   * @deprecated
   */
  badgeLabel?: string;
  /** Optional badge. Defaults to `info` signal when no `signal` is provided. */
  badge?: TSelectionInputBadge;
  assetName?: TAsset;
  assetSize?: 'm' | 'l';
  name?: string;
  /** Optional price text to display */
  price?: string;
  /** Optional slashed (original) price to display alongside price */
  slashedPrice?: string;
  /** Optional children to render below the main content */
  children?: React.ReactNode;
  /**
   * Renders the mixed (partially selected) state, for a parent checkbox whose children are
   * only partly selected. Like the native `indeterminate` DOM property it is independent of
   * `isChecked` and takes visual precedence over it, and the component keeps the property on
   * the input in sync for you — no `inputRef` mutation needed.
   */
  isIndeterminate?: boolean;
} & React.HTMLAttributes<any>;

const CheckMark = () => (
  <svg
    className="hz-CheckboxCheckmark"
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M11.3928 3.73938C11.5614 3.89876 11.6598 4.11856 11.6664 4.35047C11.6729 4.58237 11.5871 4.80739 11.4278 4.97605L6.46948 10.226C6.38912 10.311 6.29255 10.3789 6.18548 10.4259C6.0784 10.4729 5.963 10.498 5.84609 10.4996C5.72917 10.5012 5.61312 10.4794 5.50478 10.4354C5.39644 10.3915 5.29801 10.3262 5.21531 10.2435L2.59031 7.61855C2.43575 7.45268 2.35161 7.23329 2.35561 7.0066C2.3596 6.77992 2.45144 6.56364 2.61175 6.40332C2.77207 6.24301 2.98835 6.15118 3.21503 6.14718C3.44172 6.14318 3.66111 6.22732 3.82698 6.38188L5.81614 8.36988L10.1561 3.77438C10.3155 3.60579 10.5353 3.5074 10.7672 3.50084C10.9991 3.49428 11.2242 3.58008 11.3928 3.73938Z"
      fill="currentColor"
    />
  </svg>
);

const IndeterminateIcon = () => (
  <svg
    className="hz-CheckboxIndeterminate"
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M2 7C2 6.73478 2.0439 6.48043 2.12204 6.29289C2.20018 6.10536 2.30616 6 2.41667 6H11.5833C11.6938 6 11.7998 6.10536 11.878 6.29289C11.9561 6.48043 12 6.73478 12 7C12 7.26522 11.9561 7.51957 11.878 7.70711C11.7998 7.89464 11.6938 8 11.5833 8H2.41667C2.30616 8 2.20018 7.89464 2.12204 7.70711C2.0439 7.51957 2 7.26522 2 7Z"
      fill="currentColor"
    />
  </svg>
);

const Checkbox = ({
  value,
  id,
  labelText,
  isChecked,
  isIndeterminate,
  counter,
  icon,
  helperText,
  inputRef,
  ariaLabel,
  onChange,
  badgeLabel,
  badge,
  assetName,
  assetSize,
  price,
  slashedPrice,
  children,
  ...rest
}: TCheckbox): ReactElement => (
  <SelectionInput
    isChecked={isChecked}
    isIndeterminate={isIndeterminate}
    value={value}
    labelText={labelText}
    counter={counter}
    componentName={componentName}
    icon={icon}
    onChange={onChange}
    id={id}
    helperText={helperText}
    CheckMark={CheckMark}
    IndeterminateIcon={IndeterminateIcon}
    inputRef={inputRef}
    ariaLabel={ariaLabel}
    badge={badge ?? (badgeLabel ? { label: badgeLabel } : undefined)}
    assetName={assetName}
    assetSize={assetSize}
    price={price}
    slashedPrice={slashedPrice}
    children={children}
    {...rest}
  />
);

export default Checkbox;
